import { useCallback, useState } from 'react'

import { Pagination } from '@wartek-id/pagination'

import { trackClickDetailNpsn } from '@/utils/tracker/modules/datainduk'
import { TrackerSection, TrackerSubSection } from '@/utils/tracker/shared'
import { BasicDataRow } from '@/apis/types'
import { useFetchSekolahNaungan } from '@/apis/useDataIndukYayasan'

import { EmptyState } from '@/components/EmptyState'
import { ResponsiveModal } from '@/components/ResponsiveModal/ResponsiveModal'
import { RoundedTable } from '@/components/RoundedTable/RoundedTable'
import { RoundedTableLoader } from '@/components/RoundedTable/RoundedTableLoader'

import { SATPEN_TABLE__EXCLUDE_ALAMAT_COLUMN } from '../../components/CustomTable/SatpenTable/columns'
import { SatpenCellRenderer } from '../../components/CustomTable/SatpenTable/SatpenCellRenderer'
import { SatdikDetailResponsive } from '../../components/SatdikDetail/SatdikDetailResponsive'
import { DEFAULT_LIMIT } from '../../helpers'

export const SekolahNaungan = ({
  show,
  onClose,
  yayasanId,
  name,
}: {
  show: boolean
  onClose: () => void
  yayasanId: string
  name: string
}) => {
  const [page, setPage] = useState<number>(1)
  const [offset, setOffset] = useState<number>(0)
  const [selectedNpsn, setSelectedNpsn] = useState<string>('')
  const [isShowNpsnDialog, setShowNpsnDialog] = useState<boolean>(false)

  const { data: dataSekolahNaungan, status: statusFetch } =
    useFetchSekolahNaungan(offset, yayasanId, {
      enabled: Boolean(show && !!yayasanId),
    })

  const handleChangePage = useCallback((newPage: number) => {
    if (newPage <= 1) {
      setPage(1)
      setOffset(0)
    } else {
      setPage(newPage)
      setOffset((newPage - 1) * DEFAULT_LIMIT)
    }
  }, [])

  const handleOpenNpsnDialog = useCallback((row: BasicDataRow) => {
    setShowNpsnDialog(true)
    setSelectedNpsn(row?.['npsn'])
  }, [])

  const handleCloseDialogNpsn = useCallback(() => {
    setShowNpsnDialog(false)
    setSelectedNpsn('')
  }, [])

  return (
    <>
      <ResponsiveModal
        show={show}
        onClose={onClose}
        title={`Sekolah Naungan ${name}`}
        size="lg"
      >
        <section className="min-h-[200px] p-4" id="table-sekolah-naungan">
          {statusFetch === 'pending' ? (
            <RoundedTableLoader rows={5} />
          ) : (
            <>
              {(dataSekolahNaungan?.data || []).length > 0 ? (
                <>
                  <RoundedTable
                    columns={SATPEN_TABLE__EXCLUDE_ALAMAT_COLUMN}
                    withSummary={false}
                    data={(dataSekolahNaungan?.data || []).map(
                      (item, index) => ({
                        no: `${
                          index + 1 + (dataSekolahNaungan?.meta?.offset || 0)
                        }`,
                        ...item,
                      })
                    )}
                    CustomCellRenderer={({ row, col }) => {
                      return (
                        <SatpenCellRenderer
                          row={row}
                          col={col}
                          onClickNpsn={(row) => {
                            handleOpenNpsnDialog(row)
                            trackClickDetailNpsn({
                              npsn: row.npsn,
                              section: TrackerSection.YAYASAN,
                              subSection: TrackerSubSection.PAUD_DIKDASMEN,
                            })
                          }}
                        />
                      )
                    }}
                  />
                  <div
                    className="mt-4 flex items-center justify-center"
                    data-testid="pagination-root"
                  >
                    <Pagination
                      currentPage={page}
                      pageSize={DEFAULT_LIMIT}
                      onPageChange={handleChangePage}
                      totalCount={dataSekolahNaungan?.meta?.total || 0}
                    />
                  </div>
                </>
              ) : (
                <EmptyState
                  title="Data tidak ditemukan"
                  description={`Tidak dapat menemukan sekolah naungan untuk ${name}, silahkan pilih data lainnya.`}
                  size="sm"
                />
              )}
            </>
          )}
        </section>
      </ResponsiveModal>

      {show && !!yayasanId && statusFetch === 'success' ? (
        <SatdikDetailResponsive
          npsn={selectedNpsn}
          show={isShowNpsnDialog}
          onClose={handleCloseDialogNpsn}
          withPtkSummary={false}
        />
      ) : null}
    </>
  )
}
